import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { Drawer } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import {
  selectedHotelSelector,
  allPropertyConfigsSelector,
  fetchAllPropertyConfigData,
} from '../../property/propertySlice';
import SpaInfoList from './spaInfo/spaInfo-list/SpaInfo-List';
import SpaInfoView from './spaInfo/spaInfo-view/SpaInfo-View';
import SpaInfoCreate from './spaInfo/spaInfo-create/SpaInfo-Create';
import TreatmentsList from './treatments/treatments-list/Treatments-List';
import TreatmentsView from './treatments/treatments-view/Treatments-View';
import TreatmentsCreate from './treatments/treatments-create/Treatments-Create';
import CategoryList from './category/category-list/Category-List';
import CategoryView from './category/category-view/Category-View';
import CategoryCreate from './category/category-create/Category-Create';
import {
  SPA_TYPE_INFO,
  SPA_TYPE_CATEGORY,
  SPA_TYPE_TREATMENTS,
  SPA_INFO_VIEW,
  SPA_INFO_CREATE,
  SPA_INFO_EDIT,
  CATEGORY_VIEW,
  CATEGORY_CREATE,
  CATEGORY_EDIT,
  TREATMENTS_VIEW,
  TREATMENTS_CREATE,
  TREATMENTS_EDIT,
} from '../../../constants/SpaConst';
import SyncButton from '../../../shared/sync-button/Sync-Button';
import './Spa.scss';
import { validateUserSession } from '../../../utils/authUtils';
import { syncSrs } from '../../../utils/PropertyAPIUtils';
import { notificationManager } from '../../../utils/NotificationUtils';
import { generatedElapsedTime } from '../../../utils/dashboardUtils';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_HOTEL_SPA,
} from '../../../constants/RoleConsts';

const useStyles = makeStyles((theme) => ({
  activeButton: {
    color: '#29E67C',
    borderRadius: '26px',
    border: '2px solid #74E287',
    opacity: 1,
    fontWeight: 600,
    fontSize: '1.2em',
  },
  button: {
    borderRadius: '26px',
    fontWeight: 600,
    fontSize: '1.2em',
  },
  input: {
    display: 'none',
  },
}));

const Spa = (props) => {
  const { permissions, showLoader = () => {} } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const search = useLocation().search;
  const tabToOpen = new URLSearchParams(search).get('tab');
  const [activeTab, setActiveTab] = useState(tabToOpen || SPA_TYPE_INFO);
  const [state, setState] = useState({ type: SPA_INFO_CREATE, status: false });
  const selectedHotel = useSelector(selectedHotelSelector);
  const propertyConfigs = useSelector(allPropertyConfigsSelector);
  const [viewState, setViewState] = useState({
    type: SPA_INFO_VIEW,
    status: false,
    obj: null,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [lastSync, setLastSync] = useState(0);
  const history = useHistory();

  useEffect(() => {
    if (selectedHotel) {
      dispatch(fetchAllPropertyConfigData(selectedHotel));
    }
  }, [selectedHotel]);

  useEffect(() => {
    validateUserSession(history);
  }, [activeTab, history]);

  useEffect(() => {
    if (propertyConfigs?.spa) {
      let timeDifference = generatedElapsedTime(
        moment(),
        moment.unix(propertyConfigs?.spa?.lastSyncedAt),
        true,
      );
      setLastSync(timeDifference);
    }
  }, [propertyConfigs]);

  useEffect(() => {
    if (
      activeTab === SPA_TYPE_INFO ||
      activeTab === SPA_TYPE_CATEGORY ||
      activeTab === SPA_TYPE_TREATMENTS
    ) {
      setPage(0);
      setRowsPerPage(5);
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedHotel && viewState.status) {
      setActiveTab(SPA_TYPE_INFO);
      setViewState({
        type: '',
        status: false,
        obj: null,
      });
    }
  }, [selectedHotel]);

  const onDrawerToggle = (type, status, obj) => {
    setState({ type: type, status: status, obj: obj });
  };

  const onViewToggle = (type, status, obj) => {
    setViewState({ type: type, status: status, obj: obj });
  };

  const toggleDrawer = (open) => (event, reason) => {
    if (
      reason === 'backdropClick' ||
      (event?.type === 'keydown' && (event?.key === 'Tab' || event?.key === 'Shift'))
    ) {
      return;
    }
    setState(open);
  };

  const switchDrawerContent = (state) => {
    switch (state.type) {
      case CATEGORY_CREATE:
        return (
          <CategoryCreate
            toggleDrawer={onDrawerToggle}
            hotelId={selectedHotel}
            showLoader={showLoader}
          ></CategoryCreate>
        );
      case CATEGORY_EDIT:
        return (
          <CategoryCreate
            toggleDrawer={onDrawerToggle}
            category={state.obj}
            hotelId={selectedHotel}
            showLoader={showLoader}
          ></CategoryCreate>
        );
      case TREATMENTS_CREATE:
        return (
          <TreatmentsCreate
            toggleDrawer={onDrawerToggle}
            hotelId={selectedHotel}
            showLoader={showLoader}
          ></TreatmentsCreate>
        );
      case TREATMENTS_EDIT:
        return (
          <TreatmentsCreate
            toggleDrawer={onDrawerToggle}
            treatment={state.obj}
            hotelId={selectedHotel}
            showLoader={showLoader}
          ></TreatmentsCreate>
        );
      case SPA_INFO_CREATE:
        return (
          <SpaInfoCreate
            toggleDrawer={onDrawerToggle}
            hotelId={selectedHotel}
            showLoader={showLoader}
          ></SpaInfoCreate>
        );
      case SPA_INFO_EDIT:
        return (
          <SpaInfoCreate
            toggleDrawer={onDrawerToggle}
            spaInfoItem={state.obj}
            hotelId={selectedHotel}
            showLoader={showLoader}
          ></SpaInfoCreate>
        );
      default:
        break;
    }
  };

  const switchViewContent = (inpState) => {
    switch (inpState.type) {
      case SPA_INFO_VIEW:
        return (
          <SpaInfoView
            spaInfo={inpState.obj}
            onViewToggle={onViewToggle}
            permissions={permissions}
            toggleDrawer={onDrawerToggle}
            selectedHotel={selectedHotel}
          ></SpaInfoView>
        );
      case CATEGORY_VIEW:
        return (
          <CategoryView
            category={inpState.obj}
            onViewToggle={onViewToggle}
            permissions={permissions}
            toggleDrawer={onDrawerToggle}
            hotelId={selectedHotel}
          ></CategoryView>
        );
      case TREATMENTS_VIEW:
        return (
          <TreatmentsView
            treatments={inpState.obj}
            onViewToggle={onViewToggle}
            permissions={permissions}
            toggleDrawer={onDrawerToggle}
            hotelId={selectedHotel}
          ></TreatmentsView>
        );
      default:
        break;
    }
  };

  const onClickSync = async (e) => {
    e.preventDefault();

    try {
      const res = await syncSrs(selectedHotel);
      notificationManager(
        res?.data?.syncSRS?.status ? 'success' : 'danger',
        res?.data?.syncSRS?.message,
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Drawer
        anchor={'right'}
        open={state.status}
        onClose={toggleDrawer(false)}
        className='right-drawer'
      >
        {switchDrawerContent(state)}
      </Drawer>
      {viewState.status && <>{switchViewContent(viewState)}</>}
      {!viewState.status && (
        <div className='container mt-4'>
          <div className='row d-flex'>
            <div className='col-md-6'>
              <span className='page-title'>Spa</span>
            </div>

            {propertyConfigs?.spa?.isEnabled &&
              permissions[RBAC_TYPE_HOTEL_SPA] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                <div className='col-md-6 d-flex justify-content-end'>
                  <SyncButton lastSync={lastSync} onClickSync={onClickSync} />
                </div>
              )}
          </div>
          <div className='row d-flex mt-4 accommodation-list'>
            <div className='d-flex flex-wrap'>
              <Button
                size='large'
                variant={activeTab === SPA_TYPE_INFO ? 'outlined' : 'text'}
                className={
                  activeTab === SPA_TYPE_INFO ? classes.activeButton : classes.button + 'showpopup'
                }
                onClick={() => setActiveTab(SPA_TYPE_INFO)}
              >
                Info
              </Button>
              <Button
                size='large'
                variant={activeTab === SPA_TYPE_CATEGORY ? 'outlined' : 'text'}
                className={
                  activeTab === SPA_TYPE_CATEGORY
                    ? classes.activeButton
                    : classes.button + 'showpopup'
                }
                onClick={() => setActiveTab(SPA_TYPE_CATEGORY)}
              >
                Category
              </Button>
              <Button
                size='large'
                variant={activeTab === SPA_TYPE_TREATMENTS ? 'outlined' : 'text'}
                className={
                  activeTab === SPA_TYPE_TREATMENTS
                    ? classes.activeButton
                    : classes.button + 'showpopup'
                }
                onClick={() => setActiveTab(SPA_TYPE_TREATMENTS)}
              >
                Treatments
              </Button>
            </div>
          </div>
          <div className='row mx-2 d-flex mt-4'>
            <div className='card shadow'>
              {activeTab === SPA_TYPE_INFO && (
                <SpaInfoList
                  onDrawerToggle={onDrawerToggle}
                  onViewToggle={onViewToggle}
                  permissions={permissions}
                  selectedHotel={selectedHotel}
                  state={state}
                  onsetPage={(e) => setPage(e)}
                  page={page}
                  onsetRowsPerPage={(e) => setRowsPerPage(e)}
                  rowsPerPage={rowsPerPage}
                />
              )}
              {activeTab === SPA_TYPE_CATEGORY && (
                <CategoryList
                  onDrawerToggle={onDrawerToggle}
                  onViewToggle={onViewToggle}
                  permissions={permissions}
                  selectedHotel={selectedHotel}
                  state={state}
                  onsetPage={(e) => setPage(e)}
                  page={page}
                  onsetRowsPerPage={(e) => setRowsPerPage(e)}
                  rowsPerPage={rowsPerPage}
                />
              )}
              {activeTab === SPA_TYPE_TREATMENTS && (
                <TreatmentsList
                  onDrawerToggle={onDrawerToggle}
                  onViewToggle={onViewToggle}
                  permissions={permissions}
                  selectedHotel={selectedHotel}
                  state={state}
                  onsetPage={(e) => setPage(e)}
                  page={page}
                  onsetRowsPerPage={(e) => setRowsPerPage(e)}
                  rowsPerPage={rowsPerPage}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Spa;
